/**
 *
 * Shared logic for variant validation and image management across
 * EnhancedProductCard and ProductDetailPanel components.
 */
/**
 * Option value at product option index. Prefer `variant.options[i]` (Shopify .js shape);
 * fall back to option1 / option2 / option3 when the array is missing, sparse, or misaligned
 * (common with merged catalog + integration payloads, e.g. Buy Blvd GraphQL + Shopify).
 */
export const getVariantOptionAtPosition = (variant, optionIndex) => {
    if (variant == null || optionIndex < 0 || optionIndex > 2)
        return undefined;
    const fromArr = variant.options?.[optionIndex];
    if (fromArr != null && String(fromArr).trim() !== '') {
        return String(fromArr).trim();
    }
    const key = optionIndex === 0 ? 'option1' : optionIndex === 1 ? 'option2' : 'option3';
    const fb = variant[key];
    if (fb != null && String(fb).trim() !== '') {
        return String(fb).trim();
    }
    return undefined;
};
const optionValuesEqual = (a, b) => {
    if (a == null || b == null)
        return false;
    return String(a).trim() === String(b).trim();
};
/**
 * Returns true if the selected variant ID exists in the variants array.
 * Simple validation for cases where you already have a specific variant ID.
 */
export const validateVariantSelection = (selectedVariantId, variants) => {
    if (!selectedVariantId || !variants?.length) {
        return false;
    }
    return variants.some((v) => v.id?.toString() === selectedVariantId);
};
/**
 * Creates intelligent mapping of variant IDs to images with fallback strategy.
 * Implements priority-based algorithm with specificity scoring.
 */
export const buildVariantImageDictionary = (productData) => {
    const dictionary = {};
    if (!productData?.variants)
        return dictionary;
    // Group variants by each option for intelligent fallback
    const optionGroups = {};
    // Build option groups dynamically
    productData.variants.forEach((variant) => {
        const options = variant.options || [
            variant.option1,
            variant.option2,
            variant.option3,
        ].filter(Boolean);
        options.forEach((optionValue, optionIndex) => {
            const optionName = productData.options?.[optionIndex]?.name
                || `option${optionIndex + 1}`;
            if (!optionGroups[optionName]) {
                optionGroups[optionName] = {};
            }
            if (!optionGroups[optionName][optionValue]) {
                optionGroups[optionName][optionValue] = [];
            }
            optionGroups[optionName][optionValue].push(variant);
        });
    });
    productData.variants.forEach((variant) => {
        const candidates = [];
        const options = variant.options || [
            variant.option1,
            variant.option2,
            variant.option3,
        ].filter(Boolean);
        // 1. PERFECT MATCH: Variant has its own image (highest specificity)
        if (variant.featured_image?.src) {
            const imageUrl = variant.featured_image.src.startsWith('//')
                ? `https:${variant.featured_image.src}`
                : variant.featured_image.src;
            candidates.push({
                image: imageUrl,
                priority: 'perfect_match',
                source: 'variant_featured_image',
                specificity: 10000, // Highest priority - always wins
            });
        }
        else if (variant.featured_media?.preview_image?.src) {
            candidates.push({
                image: variant.featured_media.preview_image.src,
                priority: 'perfect_match',
                source: 'variant_featured_media',
                specificity: 10000, // Highest priority - always wins
            });
        }
        // 2. OPTION FAMILY MATCHES: Find variants that share option values
        if (candidates.length === 0) {
            Object.entries(optionGroups).forEach(([optionName, optionValues]) => {
                const currentOptionIndex = productData.options?.findIndex((opt) => opt.name === optionName);
                const currentOptionValue = options[currentOptionIndex];
                if (currentOptionValue && optionValues[currentOptionValue]) {
                    // Find variants in this option group that have images
                    optionValues[currentOptionValue].forEach((optionVariant) => {
                        if (optionVariant.id !== variant.id) { // Don't match self
                            let imageUrl = null;
                            if (optionVariant.featured_image?.src) {
                                imageUrl = optionVariant.featured_image.src.startsWith('//')
                                    ? `https:${optionVariant.featured_image.src}`
                                    : optionVariant.featured_image.src;
                            }
                            else if (optionVariant.featured_media?.preview_image?.src) {
                                imageUrl = optionVariant.featured_media.preview_image.src;
                            }
                            if (imageUrl) {
                                // Calculate specificity: more matching options = higher specificity
                                const otherVariantOptions = optionVariant.options || [
                                    optionVariant.option1,
                                    optionVariant.option2,
                                    optionVariant.option3,
                                ].filter(Boolean);
                                const matchingOptions = options
                                    .filter((opt, idx) => opt === otherVariantOptions[idx])
                                    .length;
                                const priority = 'option_match';
                                candidates.push({
                                    image: imageUrl,
                                    priority,
                                    source: `${optionName.toLowerCase()}_group_fallback`,
                                    specificity: 200 + (matchingOptions * 10),
                                });
                            }
                        }
                    });
                }
            });
        }
        // 3. PRODUCT FALLBACK: Use main product image (lowest specificity)
        if (candidates.length === 0) {
            let fallbackImage = null;
            if (productData.featured_image) {
                fallbackImage = productData.featured_image.startsWith('//')
                    ? `https:${productData.featured_image}`
                    : productData.featured_image;
            }
            else if (productData.images?.[0]) {
                fallbackImage = productData.images[0].startsWith('//')
                    ? `https:${productData.images[0]}`
                    : productData.images[0];
            }
            if (fallbackImage) {
                candidates.push({
                    image: fallbackImage,
                    priority: 'product_fallback',
                    source: 'product_main',
                    specificity: 100, // Lowest priority
                });
            }
        }
        // Select best candidate (highest specificity wins)
        if (candidates.length > 0) {
            const bestCandidate = candidates.sort((a, b) => b.specificity - a.specificity)[0];
            dictionary[variant.id] = bestCandidate;
        }
    });
    return dictionary;
};
/**
 * Disable button if selection is invalid, but never auto-correct.
 */
export const shouldDisableAddToCart = (isAddingToCart, isSoldOut, isAddedToCart, selectedVariantId, variants) => {
    const isValidVariantSelected = validateVariantSelection(selectedVariantId, variants);
    return !!(isAddingToCart
        || isSoldOut
        || isAddedToCart
        || !isValidVariantSelected);
};
/**
 *
 * Unified way to get the best image for a specific variant using the dictionary.
 */
export const getImageForVariant = (variantId, productData) => {
    const dictionary = buildVariantImageDictionary(productData);
    const result = dictionary[variantId];
    if (result) {
        return {
            imageUrl: result.image,
            priority: result.priority,
            source: result.source,
        };
    }
    // Fallback to product main image
    let fallbackImage = null;
    if (productData?.featured_image) {
        fallbackImage = productData.featured_image.startsWith('//')
            ? `https:${productData.featured_image}`
            : productData.featured_image;
    }
    else if (productData?.images?.[0]) {
        fallbackImage = productData.images[0].startsWith('//')
            ? `https:${productData.images[0]}`
            : productData.images[0];
    }
    return {
        imageUrl: fallbackImage,
        priority: 'product_fallback',
        source: 'product_main',
    };
};
/**
 *
 * Finds the selected variant using either selectedVariantId or selectedVariantOptions.
 */
export const getSelectedVariant = (selectedVariantId, selectedVariantOptions, variants, options = []) => {
    if (!variants?.length)
        return null;
    // First check if selection is complete
    if (options.length > 0) {
        const allOptionsSelected = Object.keys(selectedVariantOptions).length === options.length;
        if (!allOptionsSelected) {
            return null; // Incomplete selection - show price range
        }
    }
    // Method 1: Direct ID match (only if selection is complete)
    if (selectedVariantId) {
        const variant = variants.find((v) => v.id?.toString() === selectedVariantId);
        if (variant) {
            return variant;
        }
    }
    // Method 2: Options-based matching
    if (Object.keys(selectedVariantOptions).length > 0 && options.length > 0) {
        const variant = variants.find((v) => (options.every((option, index) => (optionValuesEqual(getVariantOptionAtPosition(v, index), selectedVariantOptions[option.name])))));
        if (variant) {
            return variant;
        }
    }
    return null;
};
/**
 * AUTO-SELECTION RULES
 *
 * Determines if a variant should be auto-selected:
 * - Single variant: YES (no user choice involved)
 * - Multi variant: NO (respect user choice)
 */
export const shouldAutoSelectVariant = (variants) => {
    if (!variants?.length) {
        return null;
    }
    // Only auto-select if there's exactly one variant (no choice for user)
    const result = variants.length === 1 ? variants[0] : null;
    return result;
};
/**
 * STATE SYNCHRONIZATION
 *
 * Keeps selectedVariantId and selectedVariantOptions in sync.
 * Ensures both components use consistent state management.
 */
export const syncVariantStates = (selectedVariantId, selectedVariantOptions, variants, options = []) => {
    // Find the actual variant using unified logic
    const selectedVariant = getSelectedVariant(selectedVariantId, selectedVariantOptions, variants, options);
    if (!selectedVariant) {
        return {
            syncedVariantId: null,
            syncedVariantOptions: {},
        };
    }
    // Build synced options from variant
    const syncedOptions = {};
    if (options.length > 0) {
        options.forEach((option, index) => {
            const v = getVariantOptionAtPosition(selectedVariant, index);
            if (v) {
                syncedOptions[option.name] = v;
            }
        });
    }
    return {
        syncedVariantId: selectedVariant.id?.toString() || null,
        syncedVariantOptions: syncedOptions,
    };
};
export const checkGroupAvailability = (productOptions, variants) => {
    const groups = [];
    if (!productOptions || productOptions.length === 0 || !variants || variants.length === 0) {
        return {
            groups: [],
            hasGroupWithNoOptions: false,
            autoSelectOptions: {},
        };
    }
    // Filter variants with stock
    const inStockVariants = variants.filter((v) => v.available !== false);
    productOptions.forEach((option, optionIndex) => {
        const groupName = option.name;
        const availableOptions = [];
        const unavailableOptions = [];
        // Check each option value in this group
        option.values.forEach((value) => {
            // Check if there's at least one in-stock variant with this option value
            const hasStock = inStockVariants.some((variant) => (optionValuesEqual(getVariantOptionAtPosition(variant, optionIndex), value)));
            if (hasStock) {
                availableOptions.push(value);
            }
            else {
                unavailableOptions.push(value);
            }
        });
        const hasAnyAvailable = availableOptions.length > 0;
        const hasOnlyOneAvailable = availableOptions.length === 1;
        const singleAvailableOption = hasOnlyOneAvailable ? availableOptions[0] : null;
        groups.push({
            groupName,
            availableOptions,
            unavailableOptions,
            hasAnyAvailable,
            hasOnlyOneAvailable,
            singleAvailableOption,
        });
    });
    // Check if any group has no available options
    const hasGroupWithNoOptions = groups.some((group) => !group.hasAnyAvailable);
    return {
        groups,
        hasGroupWithNoOptions,
        autoSelectOptions: {},
    };
};
/**
 * COMPLETE VARIANT VALIDATION
 * Validates that all required options are selected and variant exists with stock.
 * This is the correct validation to use for Add to Cart button state.
 */
export const isCompleteVariantSelection = (selectedVariantOptions, productOptions, variants) => {
    // Special case: Single variant products don't need option selection
    if (variants.length === 1 && productOptions.length === 0) {
        const singleVariant = variants[0];
        return {
            isComplete: true,
            selectedVariant: singleVariant,
            hasStock: singleVariant.available !== false,
        };
    }
    // Check group availability before validating selection
    if (productOptions.length > 0 && variants.length > 0) {
        const groupAvailability = checkGroupAvailability(productOptions, variants);
        // If any group has no available options, product is out of stock
        if (groupAvailability.hasGroupWithNoOptions) {
            return {
                isComplete: true,
                selectedVariant: null,
                hasStock: false,
                autoSelectOptions: groupAvailability.autoSelectOptions,
            };
        }
    }
    // 1. Check if all required options are selected
    const allOptionsSelected = productOptions.every((option) => {
        const selectedValue = selectedVariantOptions[option.name];
        return selectedValue && selectedValue.trim() !== '';
    });
    if (!allOptionsSelected) {
        // Return auto-select options if available
        const groupAvailability = productOptions.length > 0 && variants.length > 0
            ? checkGroupAvailability(productOptions, variants)
            : { autoSelectOptions: {} };
        return {
            isComplete: false,
            selectedVariant: null,
            hasStock: false,
            autoSelectOptions: groupAvailability.autoSelectOptions,
        };
    }
    // 2. Find the matching variant using existing helper
    const selectedVariant = getSelectedVariant(null, // No direct ID
    selectedVariantOptions, variants, productOptions);
    if (!selectedVariant) {
        return {
            isComplete: false,
            selectedVariant: null,
            hasStock: false,
        };
    }
    // 3. Check stock availability
    const hasStock = selectedVariant.available !== false;
    return {
        isComplete: true,
        selectedVariant,
        hasStock,
    };
};
/**
 * GET VARIANT IMAGE URL
 * Unified way to get the best image for a variant using dictionary + fallback.
 * Replaces duplicated logic across components.
 */
export const getVariantImageUrl = (variant, productData, fallbackImage) => {
    // Try dictionary first (most accurate)
    if (productData) {
        const dictionary = buildVariantImageDictionary(productData);
        const dictionaryResult = dictionary[variant.id];
        if (dictionaryResult) {
            return dictionaryResult.image;
        }
    }
    // Fallback to basic variant properties
    if (variant.featured_image?.src) {
        return variant.featured_image.src.startsWith('//')
            ? `https:${variant.featured_image.src}`
            : variant.featured_image.src;
    }
    if (variant.featured_media?.preview_image?.src) {
        return variant.featured_media.preview_image.src;
    }
    if (variant.image) {
        return variant.image;
    }
    // Final fallback
    return fallbackImage || null;
};
/**
 * Find the best variant that matches the selected options (exact or partial)
 * Uses the image dictionary for intelligent specificity scoring
 */
export const getBestMatchingVariant = (selectedOptions, variants, productData) => {
    if (!productData || variants.length === 0) {
        return null;
    }
    // Try exact match first
    const exactMatch = getSelectedVariant(null, selectedOptions, variants, productData.options || []);
    if (exactMatch) {
        return { variant: exactMatch, matchType: 'exact' };
    }
    // If no exact match, find the best partial match using image dictionary
    const imageDictionary = buildVariantImageDictionary(productData);
    const selectedEntries = Object.entries(selectedOptions).filter(([, value]) => value && value !== '');
    let bestEntry = null;
    // Find variants that match at least one selected option
    variants.forEach((variant) => {
        if (!productData.options)
            return;
        // Check if this variant matches any of the selected options
        let matchesSelection = selectedEntries.length === 0; // If no selection, consider all variants
        const matchedOptions = [];
        selectedEntries.forEach(([optionName, optionValue]) => {
            const optionIndex = productData.options.findIndex((opt) => opt.name === optionName);
            if (optionIndex !== -1
                && optionValuesEqual(getVariantOptionAtPosition(variant, optionIndex), optionValue)) {
                matchesSelection = true;
                matchedOptions.push(`${optionName}:${optionValue}`);
            }
        });
        if (matchesSelection) {
            const entry = imageDictionary[variant.id];
            const matchedCount = matchedOptions.length;
            if (entry?.image) {
                // Prioritize by matched options count FIRST, then by specificity
                const isBetter = !bestEntry
                    || matchedCount > bestEntry.matchedCount
                    || (matchedCount === bestEntry.matchedCount && entry.specificity > bestEntry.specificity);
                if (isBetter) {
                    bestEntry = { variant, specificity: entry.specificity, matchedCount };
                }
            }
        }
    });
    if (bestEntry !== null) {
        const typedEntry = bestEntry;
        return { variant: typedEntry.variant, matchType: 'partial' };
    }
    return null;
};
export const getButtonState = (selectedVariantOptions, productOptions, variants, isAddingToCart, isAddedToCart, buttonTexts = {}) => {
    // Default texts (fallback values)
    const texts = {
        addToBag: buttonTexts.addToBag || 'Add to Bag',
        addedToCart: buttonTexts.addedToCart || 'Added to Cart',
        loading: buttonTexts.loading || 'Adding...',
        soldOut: buttonTexts.soldOut || 'Sold Out',
        selectAllOptions: buttonTexts.selectAllOptions || 'Please select all options',
    };
    // Handle loading state first
    if (isAddingToCart) {
        return {
            text: texts.loading,
            disabled: true,
            showCheckout: false,
            showSuccessMessage: false,
            isSoldOut: false,
            isLoading: true,
        };
    }
    // Handle added to cart state
    if (isAddedToCart) {
        return {
            text: texts.addedToCart,
            disabled: true,
            showCheckout: true,
            showSuccessMessage: true,
            isSoldOut: false,
            isLoading: false,
        };
    }
    // Check if ALL variants are out of stock before validating selection
    // If all variants are out of stock, show "Sold Out" regardless of user selection
    const allVariantsOutOfStock = variants.length > 0
        && variants.every((variant) => variant.available === false);
    if (allVariantsOutOfStock) {
        return {
            text: texts.soldOut,
            disabled: true,
            showCheckout: false,
            showSuccessMessage: false,
            isSoldOut: true,
            isLoading: false,
        };
    }
    // Get complete validation result (includes group-level checks)
    const validation = isCompleteVariantSelection(selectedVariantOptions, productOptions, variants);
    // If validation indicates out of stock due to group-level check, return sold out
    if (validation.isComplete && !validation.hasStock && validation.selectedVariant === null) {
        return {
            text: texts.soldOut,
            disabled: true,
            showCheckout: false,
            showSuccessMessage: false,
            isSoldOut: true,
            isLoading: false,
        };
    }
    // No selection or partial selection
    if (!validation.isComplete) {
        return {
            text: texts.selectAllOptions,
            disabled: true,
            showCheckout: false,
            showSuccessMessage: false,
            isSoldOut: false,
            isLoading: false,
        };
    }
    // Complete selection + stock available
    if (validation.hasStock) {
        return {
            text: texts.addToBag,
            disabled: false,
            showCheckout: false,
            showSuccessMessage: false,
            isSoldOut: false,
            isLoading: false,
        };
    }
    // Complete selection + sold out
    return {
        text: texts.soldOut,
        disabled: true,
        showCheckout: false,
        showSuccessMessage: false,
        isSoldOut: true,
        isLoading: false,
    };
};
//# sourceMappingURL=variantValidationUtils.js.map